/** Mirrors notifications/serializers.py + models.NotificationKind (backend ADR-019). */

export type NotificationKind =
  | 'homework_assigned'
  | 'homework_graded'
  | 'homework_returned'
  | 'test_assigned'
  | 'attempt_graded'
  | 'announcement'
  | 'message'
  | 'invitation'
  | 'project_assigned'
  | 'project_graded'
  | 'due_soon';

/** One inbox row (`NotificationSerializer`). */
export interface NotificationDto {
  public_id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  /** Relative SPA path the notification deep-links to; may be blank. */
  link: string;
  is_read: boolean;
  read_at: string | null;
  created: string;
}

export interface NotificationListParams {
  page?: number;
  page_size?: number;
  is_read?: boolean;
  kind?: NotificationKind;
}

export interface UnreadCountDto {
  unread: number;
}

/**
 * The `user:` channel nudge (backend realtime/events.py).
 *
 * `unread_count` is null for roster fan-outs: a school-wide announcement cannot
 * carry a per-member count in a shared payload, so the badge re-reads the count
 * instead (backend ADR-021).
 */
export interface NotificationPublication {
  type: 'notification';
  kind: NotificationKind;
  unread_count: number | null;
}

/** The `user:` channel also carries announcement nudges. */
export interface AnnouncementPublication {
  type: 'announcement.created';
  announcement: string;
  title: string;
}

export type UserChannelPublication = NotificationPublication | AnnouncementPublication;

/** `NotificationPreference.digest` (notifications/models.py DigestMode). */
export type DigestMode = 'instant' | 'daily' | 'weekly' | 'off';

export const DIGEST_MODES: readonly DigestMode[] = ['instant', 'daily', 'weekly', 'off'] as const;

/**
 * `GET|PATCH /api/notification-preferences/` — a singleton per membership, not
 * a collection: preferences hang off the caller's Member.
 *
 * `in_app` is advisory server-side (the inbox row is always written); it gates
 * the badge and the bell here. The runtime default of `digest` is role-aware —
 * parents start on `weekly` (PRD §10), everyone else on `instant`.
 */
export interface NotificationPreferenceDto {
  public_id: string;
  in_app: boolean;
  email: boolean;
  digest: DigestMode;
}

export interface NotificationPreferenceUpdateInput {
  in_app?: boolean;
  email?: boolean;
  digest?: DigestMode;
}

export const NOTIFICATION_KINDS: readonly NotificationKind[] = [
  'homework_assigned',
  'homework_graded',
  'homework_returned',
  'test_assigned',
  'attempt_graded',
  'announcement',
  'message',
  'invitation',
  'project_assigned',
  'project_graded',
  'due_soon',
] as const;
